/**
 * RENDER-BOUNDARY EM-DASH SANITIZER — house style is EN-DASH ONLY.
 *
 * ── WHY THIS EXISTS ─────────────────────────────────────────────────────────
 * The academic-editorial white house style ("house" preset) forbids the em-dash
 * (U+2014). The DOCX/PPTX EXPORT serializers already enforce it — DOCX swaps
 * U+2014 → U+2013 (en-dash) at every text-bearing helper via `enDashOnly`
 * (`src/lib/export/docx/docxParagraph.ts`), and the PPTX path does the same. But
 * the ON-SCREEN view + the browser-print / PagedJS PDF render tree
 * (`SiteRagReportView` / `PaginatedReportView`) had NO equivalent, so an em-dash
 * sitting in DATA (siteName, evidence cells, narrative prose) rendered RAW on the
 * viewer and in the PDF while the exported DOCX/PPTX were clean — a house-style
 * divergence between the surfaces.
 *
 * This is the ONE shared util the view + paged surfaces apply at their report-
 * resolution boundary (`prepareReportForRender`), mirroring `enDashOnly`. The
 * deck running-header / footer chrome (built outside the report body from the
 * site name) reuses the {@link normalizeDashes} string primitive directly.
 *
 * Normalize display prose while preserving named immutable metadata roots.
 * Literal source quotes and their hash-bound input must retain exact bytes.
 * The EN-DASH (U+2013, used for numeric ranges such as "5–10 MW") is preserved.
 */

/** U+2014 EM DASH (—) — the glyph the house style forbids. */
const EM_DASH_RE = /—/g;
/** U+2013 EN DASH (–) — the house-style replacement (and the range glyph we keep). */
const EN_DASH = '–';

/**
 * String primitive — replace em-dash (U+2014) with en-dash (U+2013). Mirrors the
 * export path's `enDashOnly`: a falsy / empty input passes through unchanged so
 * the caller can pipe a `string` straight in. The EN-DASH is left intact.
 */
export function normalizeDashes(text: string): string {
  return text ? text.replace(EM_DASH_RE, EN_DASH) : text;
}

/**
 * Deep, REFERENCE-PRESERVING normalizer. Recursively replaces U+2014 with U+2013
 * in every string value of `value` (nested objects + arrays). Returns the SAME
 * reference when nothing changed, so a report with no em-dash is byte-stable and
 * downstream React memos / Zustand selectors stay hot (mirrors the identity
 * short-circuit of `applyHygieneBackfill`). Non-string primitives
 * (number / boolean / null / undefined) pass through untouched, and non-plain
 * objects (e.g. class instances) are returned as-is rather than rebuilt.
 */
export function normalizeReportDashes<T>(value: T): T {
  return normalizeValue(value) as T;
}

function isPlainObject(value: object): boolean {
  const proto = Object.getPrototypeOf(value) as unknown;
  return proto === Object.prototype || proto === null;
}

function normalizeValue(value: unknown): unknown {
  if (typeof value === 'string') {
    return normalizeDashes(value);
  }
  if (Array.isArray(value)) {
    let changed = false;
    const next = value.map((item) => {
      const normalized = normalizeValue(item);
      if (normalized !== item) changed = true;
      return normalized;
    });
    return changed ? next : value;
  }
  if (value !== null && typeof value === 'object' && isPlainObject(value)) {
    let changed = false;
    const source = value as Record<string, unknown>;
    const next: Record<string, unknown> = {};
    for (const key of Object.keys(source)) {
      const current = source[key];
      // Frozen literals and their hash-bound private basis are metadata, not
      // house-style prose. Preserve the original metadata and disclosure literals.
      const normalized = key === 'demonstrationProvenance' || key === 'demonstrationSedProvenance' || key === 'demonstrationDisclosure' || key === 'gridLedStandbyPlanningAtGen' || key === 'inactiveGenerationImpactAuthorityAtGen'
        ? current : normalizeValue(current);
      if (normalized !== current) changed = true;
      next[key] = normalized;
    }
    return changed ? next : value;
  }
  // number / boolean / null / undefined / bigint / symbol / function / class
  // instance — nothing to normalize.
  return value;
}
